import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'utils/request';
import { format } from 'utils/time/format';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';
import getDaysBetween from 'utils/time/getDaysBetween';
import { CJEvent } from 'types/event';
import { addToCJPastAtom } from 'containers/CahierJournal/queries/historyAtoms';
import useNotification from 'containers/App/Notifications/useNotification';
import { useAtom } from 'jotai';
import { CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY } from 'containers/CahierJournal/queries/useGetDaysWithEvents';
import { useTranslation } from 'react-i18next';

interface Props {
  from: Date;
  to: Date;
  schoolId?: string;
  schoolyearId?: string;
}

interface Options {
  disableUndoRedo?: boolean;
}

export function useDeleteCJSeancesBetweenDates(options?: Options) {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();

  const queryClient = useQueryClient();
  const [, addToPast] = useAtom(addToCJPastAtom);
  const { t } = useTranslation(['cahierJournal']);
  const { mutateAsync } = useMutation({
    mutationFn: ({ from, to, schoolId, schoolyearId }: Props) => {
      const targetSchoolId = schoolId ?? school.id;
      const targetSchoolyearId = schoolyearId ?? schoolyear.id;
      const requestURL = `/schoolyears/${targetSchoolyearId}/schools/${targetSchoolId}/cahier-journal?from=${format(
        from,
        'fr-FR',
      )}&to=${format(to, 'fr-FR')}`;
      return request(requestURL, {
        method: 'DELETE',
      });
    },
    onMutate: ({ from, to, schoolId, schoolyearId }) => {
      const targetSchoolId = schoolId ?? school.id;
      const targetSchoolyearId = schoolyearId ?? schoolyear.id;
      const days: Date[] = getDaysBetween(from, to);
      const eventsToDelete = days.reduce((acc, num) => {
        const queryKey = [
          targetSchoolyearId,
          targetSchoolId,
          EVENTS_KEY,
          format(num, 'fr-FR'),
        ];
        const events = queryClient.getQueryData<CJEvent[]>(queryKey);
        return [...(events || []), ...acc];
      }, []);

      if (!options?.disableUndoRedo) {
        addToPast({
          payload: eventsToDelete,
          action: 'DELETE',
        });
      }
      days.forEach(d => {
        queryClient.setQueryData<CJEvent[]>(
          [targetSchoolyearId, targetSchoolId, EVENTS_KEY, format(d, 'fr-FR')],
          [],
        );
      });
    },
    onSuccess: (_, { schoolId, schoolyearId }) => {
      const targetSchoolId = schoolId ?? school.id;
      const targetSchoolyearId = schoolyearId ?? schoolyear.id;
      queryClient.invalidateQueries({
        queryKey: [
          targetSchoolyearId,
          targetSchoolId,
          CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY,
        ],
      });
    },
    onError: () => {
      notifyError({
        title: t('cahierJournal:messages.deleteError_other'),
      });
    },
  });
  return mutateAsync;
}
